import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';

import { mobileStatsApi } from '../api/mobileStats';

const PAGE_SIZE = 12;

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
};

const statChipSx = {
  fontSize: 12,
  height: 30,
  borderRadius: 1.5,
  fontWeight: 600,
};

const MobileRegistrationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);

  const loadStats = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const offset = (targetPage - 1) * PAGE_SIZE;
      const data = await mobileStatsApi.getRegistrationStats(PAGE_SIZE, offset, 30);
      setStats(data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Не удалось загрузить статистику регистрации'
      );
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadStats(page);
  }, [loadStats, page]);

  const dailyMax = useMemo(() => {
    const values = (stats?.daily || []).map((item) => Number(item.count || 0));
    return Math.max(1, ...values);
  }, [stats]);

  return (
    <Box
      className="home-container page-animate"
      sx={{
        px: { xs: 2, md: 3 },
        py: 3,
        maxWidth: 1400,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Paper
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: 4,
          border: '1px solid var(--border-color)',
          background: 'var(--surface-bg)',
          boxShadow: 'none',
          mb: 2,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', lg: 'center' }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(59, 130, 246, 0.12)',
                  color: '#2563EB',
                }}
              >
                <PhoneIphoneRoundedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Регистрация в мобильном приложении
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Общая динамика и последние подключения пользователей
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="outlined"
              onClick={() => loadStats(page)}
              startIcon={<AutorenewRoundedIcon />}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}
            >
              Обновить
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Всего: ${loading ? '...' : stats?.total ?? 0}`}
              sx={{ ...statChipSx, background: '#C8F2FF', color: '#2D3C42' }}
            />
            <Chip
              label={`7 дней: ${loading ? '...' : stats?.new_last_7_days ?? 0}`}
              sx={{ ...statChipSx, background: '#FFDFA7', color: '#5E4B18' }}
            />
            <Chip
              label={`30 дней: ${loading ? '...' : stats?.new_last_30_days ?? 0}`}
              sx={{ ...statChipSx, background: '#FFE79A', color: '#5E4B18' }}
            />
            <Chip
              label={`Активные: ${loading ? '...' : stats?.active_accounts ?? 0}`}
              sx={{ ...statChipSx, background: '#D6F5C8', color: '#2E5831' }}
            />
          </Stack>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', xl: '360px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 4,
            border: '1px solid var(--border-color)',
            background: 'var(--surface-bg)',
            boxShadow: 'none',
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpRoundedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Динамика за 30 дней
              </Typography>
            </Stack>

            {loading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : stats?.daily?.length ? (
              <Stack spacing={1.1}>
                {stats.daily.map((item) => {
                  const count = Number(item.count || 0);
                  const width = `${Math.max(8, Math.round((count / dailyMax) * 100))}%`;
                  return (
                    <Stack key={item.day} spacing={0.65}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(item.day).slice(0, 10)}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {count}
                        </Typography>
                      </Stack>
                      <Box
                        sx={{
                          height: 9,
                          borderRadius: 999,
                          background: 'rgba(148, 163, 184, 0.18)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width,
                            height: '100%',
                            borderRadius: 999,
                            background:
                              'linear-gradient(90deg, rgba(59,130,246,0.72) 0%, rgba(16,185,129,0.82) 100%)',
                          }}
                        />
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Пока нет данных по регистрациям
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper
          sx={{
            p: 2.5,
            borderRadius: 4,
            border: '1px solid var(--border-color)',
            background: 'var(--surface-bg)',
            boxShadow: 'none',
          }}
        >
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Последние регистрации
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={`Страница ${page}`}
                sx={{ borderRadius: 1.5 }}
              />
            </Stack>

            <Divider />

            {loading ? (
              <Stack alignItems="center" sx={{ py: 8 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <>
                <TableContainer sx={{ border: '1px solid var(--border-color)', borderRadius: 2.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Пользователь</TableCell>
                        <TableCell>Контакт</TableCell>
                        <TableCell>Дата регистрации</TableCell>
                        <TableCell>Последний вход</TableCell>
                        <TableCell>Статус</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stats?.items || []).map((row) => (
                        <TableRow hover key={row.link_id}>
                          <TableCell>{row.full_name || 'Без имени'}</TableCell>
                          <TableCell>{row.email || row.phone || 'Не указан'}</TableCell>
                          <TableCell>{formatDateTime(row.created_at)}</TableCell>
                          <TableCell>{formatDateTime(row.last_login_at)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.is_active ? 'Активен' : 'Неактивен'}
                              color={row.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!stats?.items || stats.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Пока нет регистраций
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {(stats?.total || 0) > PAGE_SIZE ? (
                  <Stack alignItems="center">
                    <Pagination
                      page={page}
                      count={Math.max(1, Math.ceil((stats?.total || 0) / PAGE_SIZE))}
                      onChange={(_, nextPage) => setPage(nextPage)}
                    />
                  </Stack>
                ) : null}
              </>
            )}
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default MobileRegistrationsPage;
